import React, { useEffect, useRef, useState } from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { ROUTES } from "../../../system/router/routes";
import { getPatientInfo } from "../../../entities/patient-stats/patient-info/patient-info.selectors";
import { DicomStudiesIcon } from "../../../shared/ui/icons/dicom-studies-icon";
import {
  resetPatientData,
  resetSearchResults,
} from "../../../system/store/reset.slice";

import * as Styled from "./patient-info-outlet.styled";

const getAgeFromDateOfBirth = (dateOfBirth: string) => {
  const [month, day, year] = dateOfBirth.split("/").map(Number);

  if (!month || !day || !year) {
    return null;
  }

  const today = new Date();
  let age = today.getFullYear() - year;
  const hasBirthdayPassed =
    today.getMonth() + 1 > month ||
    (today.getMonth() + 1 === month && today.getDate() >= day);

  if (!hasBirthdayPassed) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

const formatDateOfBirthWithAge = (dateOfBirth?: string) => {
  if (!dateOfBirth) {
    return "—";
  }

  if (/\(\d+y\)/.test(dateOfBirth)) {
    return dateOfBirth;
  }

  const age = getAgeFromDateOfBirth(dateOfBirth);

  return age === null ? dateOfBirth : `${dateOfBirth} (${age}y)`;
};

export const PatientInfoOutlet = () => {
  const { patientID } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isStretchContent, setIsStretchContent] = useState(false);

  useEffect(() => {
    document.body.classList.add("patient-stats-body");

    return () => {
      document.body.classList.remove("patient-stats-body");
    };
  }, []);

  const patientInfo = useSelector(getPatientInfo);
  const { lastName, firstName } = patientInfo || {};
  const nextPathRef = useRef<string | null>(null);

  useEffect(() => {
    const originalPushState = window.history.pushState;

    window.history.pushState = function (...args) {
      nextPathRef.current = (args[2] as string) ?? null;
      return originalPushState.apply(this, args);
    };

    return () => {
      window.history.pushState = originalPushState;
    };
  }, []);

  useEffect(() => {
    return () => {
      const nextPath = nextPathRef.current;
      if (nextPath !== ROUTES.PATIENT_STATS) {
        dispatch(resetPatientData());
        dispatch(resetSearchResults());
      }
    };
  }, [dispatch]);

  if (!patientID) return null;

  return (
    <Styled.Container>
      <Styled.Breadcrumbs>
        Search / Main Campus / Allscripts EHR / {lastName} {firstName}
      </Styled.Breadcrumbs>
      <Styled.PatientContext>
        <Styled.PatientDetails>
          <Styled.PatientName>
            {lastName} {firstName}
          </Styled.PatientName>
          <Styled.PatientData>
            <span>
              Date of Birth:{" "}
              {formatDateOfBirthWithAge(patientInfo?.dateOfBirth)}
            </span>
            <span>MRN: 091043440</span>
            <span>Account # {patientInfo?.accountNumber ?? "—"}</span>
          </Styled.PatientData>
        </Styled.PatientDetails>
        <Styled.PatientContextControls>
          <Styled.PatientControlItem
            onClick={() =>
              patientID
                ? navigate(
                    ROUTES.PATIENT_DETAILS_ROI_REPORTS.replace(
                      ":patientID",
                      patientID,
                    ),
                  )
                : null
            }
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.6667 11.667V8.88108C16.6667 8.19978 16.6667 7.85916 16.5398 7.55288C16.4129 7.24659 16.1721 7.00573 15.6903 6.52402L11.7433 2.57689C11.3275 2.16116 11.1197 1.95328 10.8621 1.83012C10.8085 1.80449 10.7537 1.78176 10.6977 1.762C10.4284 1.66699 10.1345 1.66699 9.54651 1.66699C6.84236 1.66699 5.49027 1.66699 4.57445 2.40538C4.38944 2.55456 4.22091 2.72308 4.07174 2.9081C3.33334 3.82392 3.33334 5.17601 3.33334 7.88019V11.667C3.33334 14.8097 3.33334 16.3811 4.30965 17.3573C5.28597 18.3337 6.85731 18.3337 10 18.3337M10.8333 2.08366V2.50033C10.8333 4.85735 10.8333 6.03586 11.5656 6.76809C12.2978 7.50033 13.4763 7.50033 15.8333 7.50033H16.25"
                stroke="#181818"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.1667 18.333C14.6723 17.8415 16.6667 16.5333 16.6667 15.833C16.6667 15.1328 14.6723 13.8245 14.1667 13.333M15.8333 15.833H10"
                stroke="#181818"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            ROI
          </Styled.PatientControlItem>
          <Styled.PatientControlItem>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.75 18.3333H7.91665C5.16679 18.3333 3.79185 18.3333 2.93759 17.4791C2.08331 16.6247 2.08331 15.2498 2.08331 12.5V8.33333C2.08331 5.58347 2.08331 4.20854 2.93759 3.35427C3.79185 2.5 5.16679 2.5 7.91665 2.5H10.4167C13.1665 2.5 14.5414 2.5 15.3958 3.35427C16.25 4.20854 16.25 5.58347 16.25 8.33333V9.16667"
                stroke="#181818"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.3333 1.66699V3.33366M9.16666 1.66699V3.33366M4.99997 1.66699V3.33366"
                stroke="#181818"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.83331 12.4997H9.16667M5.83331 8.33301H12.5"
                stroke="#181818"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                opacity="0.93"
                d="M17.2998 12.3991C16.5453 11.5537 16.0927 11.6041 15.5897 11.755C15.2376 11.8053 14.0304 13.2143 13.5274 13.663C12.7016 14.4789 11.872 15.3189 11.8173 15.4285C11.6609 15.6826 11.5155 16.133 11.4451 16.6361C11.3143 17.391 11.1256 18.2407 11.3646 18.3136C11.6035 18.3863 12.2699 18.2465 13.0244 18.1357C13.5274 18.0451 13.8795 17.9445 14.131 17.7936C14.4831 17.5822 15.137 16.8375 16.2637 15.7304C16.9703 14.9864 17.6519 14.4723 17.8532 13.9691C18.0543 13.2143 17.7526 12.8117 17.2998 12.3991Z"
                stroke="#181818"
                strokeWidth="1.5"
              />
            </svg>
            Reports
          </Styled.PatientControlItem>
          <Styled.PatientControlItem>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.5 14.6875C12.4387 16.2307 11.1526 17.5412 9.42967 17.499C9.02883 17.4892 8.53342 17.3495 7.5426 17.07C5.15801 16.3973 3.08796 15.2669 2.5913 12.7346C2.5 12.2692 2.5 11.7453 2.5 10.6977V9.30225C2.5 8.25467 2.5 7.73087 2.5913 7.26538C3.08796 4.73304 5.15801 3.60263 7.5426 2.93002C8.53342 2.65053 9.02883 2.51079 9.42967 2.50099C11.1526 2.45884 12.4387 3.76922 12.5 5.31251"
                stroke="#181818"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M17.5 10.0003H8.33334M17.5 10.0003C17.5 9.41683 15.8381 8.3266 15.4167 7.91699M17.5 10.0003C17.5 10.5838 15.8381 11.6741 15.4167 12.0837"
                stroke="#181818"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Exit App
          </Styled.PatientControlItem>
        </Styled.PatientContextControls>
      </Styled.PatientContext>
      <Styled.Layout>
        <Styled.MainMenu>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_STATS.replace(":patientID", patientID)}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M3.59835 16.4017C2.5 15.3033 2.5 13.5355 2.5 10C2.5 6.46447 2.5 4.6967 3.59835 3.59835C4.6967 2.5 6.46447 2.5 10 2.5C13.5355 2.5 15.3033 2.5 16.4017 3.59835C17.5 4.6967 17.5 6.46447 17.5 10C17.5 13.5355 17.5 15.3033 16.4017 16.4017C15.3033 17.5 13.5355 17.5 10 17.5C6.46447 17.5 4.6967 17.5 3.59835 16.4017Z"
                fill="#7C7F7E"
              />
              <path
                d="M3.59835 16.4017C2.5 15.3033 2.5 13.5355 2.5 10C2.5 6.46447 2.5 4.6967 3.59835 3.59835C4.6967 2.5 6.46447 2.5 10 2.5C13.5355 2.5 15.3033 2.5 16.4017 3.59835C17.5 4.6967 17.5 6.46447 17.5 10C17.5 13.5355 17.5 15.3033 16.4017 16.4017C15.3033 17.5 13.5355 17.5 10 17.5C6.46447 17.5 4.6967 17.5 3.59835 16.4017Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5 10.8332H6.25L7.5 7.49984L8.75 13.3332L10 10.8332H11.25L12.5 6.6665L13.75 10.8332H15"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <span>Problem List</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_ALLERGIES.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_3118_8545)">
                <path
                  opacity="0.4"
                  fillRule="evenodd"
                  clipRule="evenodd"
                  d="M10.0013 18.3334C14.6036 18.3334 18.3346 14.6024 18.3346 10.0001C18.3346 5.39771 14.6036 1.66675 10.0013 1.66675C5.39893 1.66675 1.66797 5.39771 1.66797 10.0001C1.66797 14.6024 5.39893 18.3334 10.0013 18.3334ZM12.5013 8.33341C13.4218 8.33341 14.168 7.58722 14.168 6.66675C14.168 5.74627 13.4218 5.00008 12.5013 5.00008C11.5808 5.00008 10.8346 5.74627 10.8346 6.66675C10.8346 7.58722 11.5808 8.33341 12.5013 8.33341Z"
                  fill="#7C7F7E"
                />
                <path
                  d="M10.0013 18.3334C14.6037 18.3334 18.3346 14.6025 18.3346 10.0001C18.3346 5.39771 14.6037 1.66675 10.0013 1.66675C5.39893 1.66675 1.66797 5.39771 1.66797 10.0001C1.66797 14.6025 5.39893 18.3334 10.0013 18.3334Z"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M12.4987 8.33333C13.4192 8.33333 14.1654 7.58714 14.1654 6.66667C14.1654 5.74619 13.4192 5 12.4987 5C11.5782 5 10.832 5.74619 10.832 6.66667C10.832 7.58714 11.5782 8.33333 12.4987 8.33333Z"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M5 10.4166L5.17453 10.6125C5.49863 10.9761 6.04502 10.8766 6.25 10.4166C6.45498 9.95665 7.00137 9.85715 7.32548 10.2208L7.5 10.4166"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.2024 15L13.2947 14.7124C13.4662 14.1783 13.0631 13.6894 12.5013 13.75C11.9395 13.8106 11.5364 13.3217 11.7079 12.7876L11.8002 12.5"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M15.8395 10.8333H15.832"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M7.50747 14.1667H7.5"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.67544 6.66675H6.66797"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_3118_8545">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <span>Allergies</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_MEDICATIONS.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M14.9981 17.6641C17.3896 16.2834 18.2089 13.2254 16.8283 10.834L8.16797 15.834C9.54867 18.2254 12.6067 19.0448 14.9981 17.6641Z"
                fill="#7C7F7E"
              />
              <path
                opacity="0.4"
                d="M6.31894 5.82805L4.13154 7.12056C2.57135 8.04245 2.03679 10.0842 2.93757 11.681C3.47916 12.641 4.4163 13.2216 5.41667 13.3317L7.64673 12.1229C7.96393 10.854 8.7755 9.71045 9.99759 9.00487C10.2613 8.85262 10.5331 8.72712 10.8099 8.62753L9.58334 6.665C8.5 4.50049 8.5 4.50046 6.31894 5.82805Z"
                fill="#7C7F7E"
              />
              <path
                d="M8.369 4.61771L12.6065 2.11385C14.1667 1.19195 16.1617 1.73904 17.0624 3.33581C17.7861 4.61864 17.5833 6.18868 16.6667 7.23452M8.369 4.61771L4.13154 7.12157C2.57136 8.04346 2.03679 10.0853 2.93757 11.682C3.47916 12.6421 4.4163 13.2227 5.41667 13.3327M8.369 4.61771L9.58333 6.66601"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16.827 10.8348C18.2078 13.2263 17.3884 16.2843 14.9969 17.665C12.6054 19.0457 9.54752 18.2263 8.1668 15.8348M16.827 10.8348C15.4463 8.44334 12.3884 7.624 9.99693 9.00467C7.60547 10.3854 6.78609 13.4433 8.1668 15.8348M16.827 10.8348L8.1668 15.8348"
                stroke="#7C7F7E"
                strokeWidth="1.5"
              />
            </svg>
            <span>Medications</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_IMMUNIZATIONS.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clipPath="url(#clip0_1249_275)">
                <path
                  opacity="0.4"
                  d="M10.0615 14.363L14.168 10.2564L9.74555 5.83398L5.63901 9.94048C4.7517 10.8278 4.30805 11.2715 4.2023 11.8032C4.15652 12.0332 4.15652 12.2702 4.2023 12.5003C4.30805 13.032 4.75171 13.4757 5.63901 14.363C6.52631 15.2502 6.96997 15.6939 7.50161 15.7997C7.73176 15.8454 7.96867 15.8454 8.19883 15.7997C8.73047 15.6939 9.17413 15.2502 10.0615 14.363Z"
                  fill="#7C7F7E"
                />
                <path
                  d="M14.168 1.66602C14.168 2.0909 14.168 2.30334 14.2244 2.50132C14.2554 2.61007 14.2988 2.71488 14.3538 2.81371C14.4539 2.99359 14.6041 3.14382 14.9046 3.44425L16.5564 5.09612C16.8568 5.39655 17.0071 5.54677 17.187 5.64686C17.2858 5.70183 17.3906 5.74526 17.4993 5.77625C17.6973 5.83268 17.9097 5.83268 18.3346 5.83268"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10.0615 14.363L14.168 10.2564L9.74555 5.83398L5.63901 9.94048C4.7517 10.8278 4.30805 11.2715 4.2023 11.8032C4.15652 12.0332 4.15652 12.2702 4.2023 12.5003C4.30805 13.032 4.75171 13.4757 5.63901 14.363C6.52631 15.2502 6.96997 15.6939 7.50161 15.7997C7.73176 15.8454 7.96867 15.8454 8.19883 15.7997C8.73047 15.6939 9.17413 15.2502 10.0615 14.363Z"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M9.16797 5L15.0013 10.8333"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M6.66536 15.5317L5.69081 16.2277C5.29859 16.5079 5.10248 16.648 4.88741 16.6649C4.804 16.6715 4.72009 16.6646 4.6389 16.6443C4.42955 16.5923 4.25913 16.4219 3.9183 16.0811C3.57747 15.7402 3.40705 15.5698 3.355 15.3605C3.3348 15.2792 3.32786 15.1953 3.33443 15.1119C3.35136 14.8969 3.49145 14.7007 3.7716 14.3086L4.46771 13.334"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M11.25 6.66732L15.4167 3.33398M13.3333 8.75065L16.6667 4.58398"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinejoin="round"
                />
                <path
                  d="M3.7513 16.25L1.66797 18.3333"
                  stroke="#7C7F7E"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_1249_275">
                  <rect width="20" height="20" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <span>Immunizations</span>
          </Styled.Link>
          <Styled.Divider />
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_APPOINTMENTS.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M17.9154 10.63V10.202C17.9154 8.76085 17.9154 7.60572 17.8501 6.66602H2.14726C2.08203 7.60572 2.08203 8.76085 2.08203 10.202V10.63C2.08203 14.2611 2.08203 16.0766 3.12546 17.2047C4.1689 18.3327 5.84827 18.3327 9.20703 18.3327H10.7904C14.1491 18.3327 15.8285 18.3327 16.8719 17.2047C17.9154 16.0766 17.9154 14.2611 17.9154 10.63Z"
                fill="#7C7F7E"
              />
              <path
                d="M15 1.66602V3.33268M5 1.66602V3.33268"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.99755 10.834H10.0051M9.99755 14.1673H10.0051M13.3271 10.834H13.3346M6.66797 10.834H6.67544M6.66797 14.1673H6.67544"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.91797 6.66602H17.0846"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.08203 10.2027C2.08203 6.57162 2.08203 4.75607 3.12546 3.62803C4.1689 2.5 5.84827 2.5 9.20703 2.5H10.7904C14.1491 2.5 15.8285 2.5 16.8719 3.62803C17.9154 4.75607 17.9154 6.57162 17.9154 10.2027V10.6307C17.9154 14.2617 17.9154 16.0773 16.8719 17.2053C15.8285 18.3333 14.1491 18.3333 10.7904 18.3333H9.20703C5.84827 18.3333 4.1689 18.3333 3.12546 17.2053C2.08203 16.0773 2.08203 14.2617 2.08203 10.6307V10.2027Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.5 6.66602H17.5"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Appointments</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_DEMOGRAPHICS.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M7.5013 2.91602C9.80247 2.91602 11.668 4.7815 11.668 7.08268C11.668 9.38385 9.80247 11.2493 7.5013 11.2493C10.723 11.2493 13.3346 13.861 13.3346 17.0827H1.66797C1.66797 13.861 4.27964 11.2493 7.5013 11.2493C5.20012 11.2493 3.33464 9.38385 3.33464 7.08268C3.33464 4.7815 5.20012 2.91602 7.5013 2.91602Z"
                fill="#7C7F7E"
              />
              <path
                d="M11.6654 7.08268C11.6654 4.7815 9.79986 2.91602 7.4987 2.91602C5.19751 2.91602 3.33203 4.7815 3.33203 7.08268C3.33203 9.38385 5.19751 11.2493 7.4987 11.2493C9.79986 11.2493 11.6654 9.38385 11.6654 7.08268Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.3346 17.0833C13.3346 13.8617 10.723 11.25 7.5013 11.25C4.27964 11.25 1.66797 13.8617 1.66797 17.0833"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.168 8.33398H18.3346"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M14.168 10.834H18.3346"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M16.668 13.334H18.3346"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Demographics</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_DOCUMENTS.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M10.8214 1.66602C10.8752 1.66602 10.9297 1.66602 10.9848 1.66602C12.3959 1.66602 13.1014 1.66602 13.6649 1.87618C14.5707 2.21406 15.2853 2.92859 15.6232 3.83447C15.8333 4.39795 15.8333 5.10347 15.8333 6.5145V14.5827C15.8333 14.9697 15.8333 15.1631 15.8172 15.3261C15.6614 16.9087 14.4093 18.1607 12.8267 18.3167C12.6637 18.3327 12.4703 18.3327 12.0833 18.3327H8.56058C6.0028 18.3327 4.72389 18.3327 3.83574 17.6678C3.58127 17.4773 3.35536 17.2514 3.16487 16.9969C2.5 16.1087 2.5 14.8299 2.5 12.2721V10.1508C2.5 10.0957 2.5 10.0412 2.5 9.98742C2.50005 9.99142 2.50007 9.99542 2.50007 9.99942C2.50007 8.46533 3.74372 7.22164 5.27785 7.22164H6.20377C6.63431 7.22164 6.84959 7.22164 7.02621 7.17432C7.5055 7.04589 7.87987 6.67152 8.0083 6.19222C8.05562 6.0156 8.05562 5.80033 8.05562 5.36979V4.44386C8.05562 2.90973 9.29925 1.66608 10.8334 1.66608C10.8294 1.66608 10.8254 1.66606 10.8214 1.66602Z"
                fill="#7C7F7E"
              />
              <path
                d="M15.8333 7.49935V6.5145C15.8333 5.10347 15.8333 4.39795 15.6232 3.83447C15.2853 2.9286 14.5707 2.21406 13.6649 1.87618C13.1014 1.66602 12.3959 1.66602 10.9848 1.66602C8.5155 1.66602 7.28088 1.66602 6.29479 2.03381C4.70952 2.62508 3.45907 3.87553 2.86779 5.46081C2.5 6.4469 2.5 7.68156 2.5 10.1508V12.2721C2.5 14.8298 2.5 16.1088 3.16486 16.9969C3.35536 17.2514 3.58127 17.4773 3.83574 17.6678C4.72389 18.3327 6.0028 18.3327 8.56058 18.3327H9.16667C10.1415 18.3327 12.0833 18.3327 12.0833 18.3327"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.16797 11.9444H9.86822C10.4196 11.9444 10.6951 11.9444 10.9167 12.0897C11.1382 12.2348 11.2615 12.4963 11.5081 13.0193L12.8346 15.8333L14.668 10L15.9946 12.814C16.2411 13.337 16.3644 13.5985 16.5859 13.7437C16.8075 13.8889 17.0831 13.8889 17.6344 13.8889H18.3346"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.5 9.99935C2.5 8.46527 3.74365 7.22157 5.27778 7.22157H6.2037C6.63425 7.22157 6.84952 7.22157 7.02614 7.17425C7.50543 7.04582 7.8798 6.67145 8.00823 6.19215C8.05556 6.01553 8.05556 5.80027 8.05556 5.36972V4.44379C8.05556 2.90967 9.29925 1.66602 10.8333 1.66602"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Documents</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_DICOM_STUDIES.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <DicomStudiesIcon />
            <span>DICOM Studies</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_ENCOUNTERS.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M16.6654 18.334V15.834C16.6654 13.477 16.6654 12.2985 15.9331 11.5662C15.2009 10.834 14.0224 10.834 11.6654 10.834L9.9987 12.5007L8.33203 10.834C5.97501 10.834 4.7965 10.834 4.06426 11.5662C3.33203 12.2985 3.33203 13.477 3.33203 15.834V18.334H16.6654Z"
                fill="#7C7F7E"
              />
              <path
                opacity="0.4"
                d="M12.9154 5.41602V4.58268C12.9154 2.97185 11.6095 1.66602 9.9987 1.66602C8.38786 1.66602 7.08203 2.97185 7.08203 4.58268V5.41602C7.08203 7.02685 8.38786 8.33268 9.9987 8.33268C11.6095 8.33268 12.9154 7.02685 12.9154 5.41602Z"
                fill="#7C7F7E"
              />
              <path
                d="M16.6654 18.334V15.834C16.6654 13.477 16.6654 12.2985 15.9331 11.5662C15.2009 10.834 14.0224 10.834 11.6654 10.834L9.9987 12.5007L8.33203 10.834C5.97501 10.834 4.7965 10.834 4.06426 11.5662C3.33203 12.2985 3.33203 13.477 3.33203 15.834V18.334"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.332 10.834V15.4173"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.08464 10.834V14.1673M7.08464 14.1673C8.00511 14.1673 8.7513 14.9135 8.7513 15.834V16.6673M7.08464 14.1673C6.16416 14.1673 5.41797 14.9135 5.41797 15.834V16.6673"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.9154 5.41602V4.58268C12.9154 2.97185 11.6095 1.66602 9.9987 1.66602C8.38786 1.66602 7.08203 2.97185 7.08203 4.58268V5.41602C7.08203 7.02685 8.38786 8.33268 9.9987 8.33268C11.6095 8.33268 12.9154 7.02685 12.9154 5.41602Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.957 16.041C13.957 16.3862 13.6772 16.666 13.332 16.666C12.9869 16.666 12.707 16.3862 12.707 16.041C12.707 15.6958 12.9869 15.416 13.332 15.416C13.6772 15.416 13.957 15.6958 13.957 16.041Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
              />
            </svg>
            <span>Encounters</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_FLOWSHEETS.replace(
              ":patientID",
              patientID,
            )}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M2.93631 3.35427C2.08203 4.20854 2.08203 5.58347 2.08203 8.33333V12.5C2.08203 15.2498 2.08203 16.6247 2.93631 17.4791C3.79057 18.3333 5.16551 18.3333 7.91536 18.3333H10.4154L11.347 18.2351C11.1846 18.0727 11.2461 17.7852 11.369 17.2102C11.4016 17.0574 11.4285 16.9004 11.4555 16.7427C11.5583 16.1415 11.6631 15.5293 12.0993 15.0931L14.8558 12.3366C15.1336 12.0587 15.2725 11.9199 15.417 11.8364C15.6724 11.689 15.9664 11.6376 16.2487 11.6822V8.33333C16.2487 5.58347 16.2487 4.20854 15.3945 3.35427C14.5401 2.5 13.1652 2.5 10.4154 2.5H7.91536C5.16551 2.5 3.79057 2.5 2.93631 3.35427Z"
                fill="#7C7F7E"
              />
              <path
                d="M8.74872 18.3333H7.91536C5.16551 18.3333 3.79057 18.3333 2.93631 17.4791C2.08203 16.6247 2.08203 15.2498 2.08203 12.5V8.33333C2.08203 5.58347 2.08203 4.20854 2.93631 3.35427C3.79057 2.5 5.16551 2.5 7.91536 2.5H10.4154C13.1652 2.5 14.5401 2.5 15.3945 3.35427C16.2487 4.20854 16.2487 5.58347 16.2487 8.33333V9.16667"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.3334 1.66602V3.33268M9.16669 1.66602V3.33268M5 1.66602V3.33268"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.83203 12.5007H9.16539M5.83203 8.33398H12.4987"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                opacity="0.93"
                d="M17.2999 12.3981C16.5454 11.5527 16.0927 11.6031 15.5897 11.754C15.2376 11.8043 14.0304 13.2133 13.5274 13.662C12.7016 14.4779 11.872 15.3179 11.8173 15.4275C11.6609 15.6817 11.5155 16.132 11.4451 16.6352C11.3143 17.39 11.1256 18.2397 11.3646 18.3126C11.6035 18.3853 12.2699 18.2455 13.0244 18.1347C13.5274 18.0442 13.8795 17.9435 14.131 17.7926C14.4831 17.5812 15.137 16.8365 16.2637 15.7294C16.9704 14.9854 17.6519 14.4713 17.8532 13.9682C18.0544 13.2133 17.7526 12.8107 17.2999 12.3981Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
              />
            </svg>

            <span>Flowsheets</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_INSURANCE.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M17.5 9.31877V6.89959C17.5 5.53292 17.5 4.84958 17.1632 4.40376C16.8265 3.95792 16.0651 3.74148 14.5423 3.3086C13.5018 3.01285 12.5847 2.65654 11.8519 2.33126C10.8528 1.88777 10.3533 1.66602 10 1.66602C9.64667 1.66602 9.14717 1.88777 8.14809 2.33126C7.41532 2.65654 6.4982 3.01284 5.45778 3.3086C3.93494 3.74148 3.17352 3.95792 2.83676 4.40376C2.5 4.84958 2.5 5.53292 2.5 6.89959V9.31877C2.5 14.0064 6.71897 16.8189 8.82833 17.9322C9.33425 18.1992 9.58717 18.3327 10 18.3327C10.4128 18.3327 10.6657 18.1992 11.1717 17.9322C13.281 16.8189 17.5 14.0064 17.5 9.31877Z"
                fill="#7C7F7E"
              />
              <path
                d="M10 7.08398V12.084M7.5 9.58398H12.5"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M17.5 9.31877V6.89959C17.5 5.53292 17.5 4.84958 17.1632 4.40376C16.8265 3.95792 16.0651 3.74148 14.5423 3.3086C13.5018 3.01285 12.5847 2.65654 11.8519 2.33126C10.8528 1.88777 10.3533 1.66602 10 1.66602C9.64667 1.66602 9.14717 1.88777 8.14809 2.33126C7.41532 2.65654 6.4982 3.01284 5.45778 3.3086C3.93494 3.74148 3.17352 3.95792 2.83676 4.40376C2.5 4.84958 2.5 5.53292 2.5 6.89959V9.31877C2.5 14.0064 6.71897 16.8189 8.82833 17.9322C9.33425 18.1992 9.58717 18.3327 10 18.3327C10.4128 18.3327 10.6657 18.1992 11.1717 17.9322C13.281 16.8189 17.5 14.0064 17.5 9.31877Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>

            <span>Insurance</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_ORDERS_PROCEDURES.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M3.59936 17.1268C4.34066 18.3333 6.22667 18.3333 9.9987 18.3333C13.7707 18.3333 15.6567 18.3333 16.398 17.1268L16.4094 17.1082C17.14 15.8955 16.2494 14.2595 14.4681 10.9876L13.9304 9.99993C12.4092 11.6666 10.6754 10.9722 9.9987 10.4166C9.48645 9.90427 8.93911 9.5886 8.42428 9.40352C7.2339 8.97552 6.13408 9.8766 5.52925 10.9876C3.74801 14.2595 2.8574 15.8955 3.58807 17.1082L3.59936 17.1268Z"
                fill="#7C7F7E"
              />
              <path
                d="M12.1158 1.66602V3.73787C12.1158 5.19449 12.1158 5.9228 12.2941 6.62197C12.4724 7.32115 12.8219 7.96313 13.5209 9.2471L14.4681 10.987C16.2494 14.2589 17.14 15.8949 16.4094 17.1077L16.398 17.1262C15.6567 18.3327 13.7707 18.3327 9.9987 18.3327C6.22667 18.3327 4.34066 18.3327 3.59936 17.1262L3.58807 17.1077C2.8574 15.8949 3.74801 14.2589 5.52925 10.987L6.47645 9.2471C7.17544 7.96313 7.52494 7.32115 7.70326 6.62197C7.88159 5.9228 7.88159 5.19449 7.88159 3.73787V1.66602"
                stroke="#7C7F7E"
                strokeWidth="1.5"
              />
              <path
                d="M7.5 13.334L7.50723 13.332"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M12.5 15.002L12.5073 15"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.66797 1.66602H13.3346"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M6.25 9.62954C7.08333 8.66838 8.41617 9.36121 10 10.2645C12.0833 11.4525 13.3333 10.541 13.75 9.67863"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span>Orders & Procedures</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_PHYSICIAN_CARE_TEAM.replace(
              ":patientID",
              patientID,
            )}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M3.73443 13.426C2.75195 13.9525 0.175949 15.0276 1.7449 16.3728C2.51133 17.03 3.36493 17.5 4.4381 17.5H10.5619C11.6351 17.5 12.4887 17.03 13.2551 16.3728C14.8241 15.0276 12.2481 13.9525 11.2656 13.426C8.96167 12.1913 6.03833 12.1913 3.73443 13.426Z"
                fill="#7C7F7E"
              />
              <path
                opacity="0.4"
                d="M16.25 6.66602C16.25 8.04672 15.1307 9.16602 13.75 9.16602C12.3692 9.16602 11.25 8.04672 11.25 6.66602C11.25 5.28531 12.3692 4.16602 13.75 4.16602C15.1307 4.16602 16.25 5.28531 16.25 6.66602Z"
                fill="#7C7F7E"
              />
              <path
                d="M15.5147 16.666H15.9232C16.8814 16.666 17.6436 16.2294 18.3279 15.619C20.0663 14.0682 15.9797 12.4993 14.5846 12.4993M12.918 4.22332C13.1072 4.18579 13.3037 4.16602 13.5053 4.16602C15.0219 4.16602 16.2513 5.28531 16.2513 6.66602C16.2513 8.04672 15.0219 9.16602 13.5053 9.16602C13.3037 9.16602 13.1072 9.14627 12.918 9.10868"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <path
                d="M3.73443 13.426C2.75195 13.9525 0.175949 15.0276 1.7449 16.3728C2.51133 17.03 3.36493 17.5 4.4381 17.5H10.5619C11.6351 17.5 12.4887 17.03 13.2551 16.3728C14.8241 15.0276 12.2481 13.9525 11.2656 13.426C8.96167 12.1913 6.03833 12.1913 3.73443 13.426Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
              />
              <path
                d="M10.8346 6.24935C10.8346 8.0903 9.34222 9.58268 7.5013 9.58268C5.66035 9.58268 4.16797 8.0903 4.16797 6.24935C4.16797 4.4084 5.66035 2.91602 7.5013 2.91602C9.34222 2.91602 10.8346 4.4084 10.8346 6.24935Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
              />
            </svg>
            <span>Physician Care Team</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_RESULTS.replace(":patientID", patientID)}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M16.6654 11.2507C16.6654 8.94948 14.7999 7.08398 12.4987 7.08398C10.1975 7.08398 8.33203 8.94948 8.33203 11.2507C8.33203 13.5518 10.1975 15.4173 12.4987 15.4173C14.7999 15.4173 16.6654 13.5518 16.6654 11.2507Z"
                fill="#7C7F7E"
              />
              <path
                d="M2.08203 7.5H5.41536"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.08203 11.666H5.41536"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.08203 3.33398H15.4154"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M15.4449 14.1969L17.9154 16.6673M16.6654 11.2507C16.6654 8.94948 14.7999 7.08398 12.4987 7.08398C10.1975 7.08398 8.33203 8.94948 8.33203 11.2507C8.33203 13.5518 10.1975 15.4173 12.4987 15.4173C14.7999 15.4173 16.6654 13.5518 16.6654 11.2507Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>Results</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_SUMMARY.replace(":patientID", patientID)}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M2.5 5.41667C2.5 4.24628 2.5 3.66109 2.83706 3.24072C2.98297 3.05873 3.17048 2.90248 3.38886 2.78088C3.89331 2.5 4.59554 2.5 6 2.5H14C15.4045 2.5 16.1067 2.5 16.6112 2.78088C16.8295 2.90248 17.017 3.05873 17.1629 3.24072C17.5 3.66109 17.5 4.24628 17.5 5.41667C17.5 6.58705 17.5 7.17224 17.1629 7.59258C17.017 7.77458 16.8295 7.93083 16.6112 8.05242C16.1067 8.33333 15.4045 8.33333 14 8.33333H6C4.59554 8.33333 3.89331 8.33333 3.38886 8.05242C3.17048 7.93083 2.98297 7.77458 2.83706 7.59258C2.5 7.17224 2.5 6.58705 2.5 5.41667Z"
                fill="#7C7F7E"
              />
              <path
                opacity="0.4"
                d="M2.5 14.5827C2.5 13.4123 2.5 12.8271 2.83706 12.4068C2.98297 12.2248 3.17048 12.0685 3.38886 11.9469C3.89331 11.666 4.59554 11.666 6 11.666H14C15.4045 11.666 16.1067 11.666 16.6112 11.9469C16.8295 12.0685 17.017 12.2248 17.1629 12.4068C17.5 12.8271 17.5 13.4123 17.5 14.5827C17.5 15.7531 17.5 16.3383 17.1629 16.7586C17.017 16.9406 16.8295 17.0968 16.6112 17.2184C16.1067 17.4994 15.4045 17.4993 14 17.4993H6C4.59554 17.4993 3.89331 17.4994 3.38886 17.2184C3.17048 17.0968 2.98297 16.9406 2.83706 16.7586C2.5 16.3383 2.5 15.7531 2.5 14.5827Z"
                fill="#7C7F7E"
              />
              <path
                d="M17.5 14.5827C17.5 15.7531 17.5 16.3383 17.184 16.7586C17.0472 16.9406 16.8714 17.0968 16.6667 17.2184C16.1937 17.4994 15.5354 17.4993 14.2187 17.4993H5.78125C4.46457 17.4993 3.80622 17.4994 3.33331 17.2184C3.12857 17.0968 2.95279 16.9406 2.81599 16.7586C2.5 16.3383 2.5 15.7531 2.5 14.5827C2.5 13.4123 2.5 12.8271 2.81599 12.4068C2.95279 12.2248 3.12857 12.0685 3.33331 11.9469C3.80622 11.666 4.46457 11.666 5.78125 11.666H14.2187C15.5354 11.666 16.1937 11.666 16.6667 11.9469C16.8714 12.0685 17.0472 12.2248 17.184 12.4068C17.5 12.8271 17.5 13.4123 17.5 14.5827Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
              />
              <path
                d="M17.5 5.41667C17.5 6.58708 17.5 7.17225 17.184 7.59258C17.0472 7.77458 16.8714 7.93083 16.6667 8.05242C16.1937 8.33333 15.5354 8.33333 14.2187 8.33333H5.78125C4.46457 8.33333 3.80622 8.33333 3.33331 8.05242C3.12857 7.93083 2.95279 7.77458 2.81599 7.59258C2.5 7.17225 2.5 6.58708 2.5 5.41667C2.5 4.24625 2.5 3.66108 2.81599 3.24075C2.95279 3.05875 3.12857 2.9025 3.33331 2.78092C3.80622 2.5 4.46457 2.5 5.78125 2.5H14.2187C15.5354 2.5 16.1937 2.5 16.6667 2.78092C16.8714 2.9025 17.0472 3.05875 17.184 3.24075C17.5 3.66108 17.5 4.24625 17.5 5.41667Z"
                stroke="#7C7F7E"
                strokeWidth="1.5"
              />
            </svg>
            <span>Summary</span>
          </Styled.Link>
          <Styled.Link
            $themeColor={"#065FA6"}
            to={ROUTES.PATIENT_DETAILS_VITALS.replace(":patientID", patientID)}
            end
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                opacity="0.4"
                d="M2.91797 11.3977C2.91797 7.9109 5.90196 4.46524 7.99641 2.4762C9.13389 1.39596 10.8687 1.39596 12.0062 2.4762C14.1006 4.46524 17.0846 7.9109 17.0846 11.3977C17.0846 14.8163 14.4023 18.3327 10.0013 18.3327C5.60029 18.3327 2.91797 14.8163 2.91797 11.3977Z"
                fill="#7C7F7E"
              />
              <path
                d="M7.5 10.8327H8.16693C8.692 10.8327 8.9545 10.8327 9.16542 10.9571C9.37642 11.0816 9.49383 11.3058 9.72858 11.754L10.9921 14.166L12.7381 9.16602L14.0015 11.578C14.2363 12.0263 14.3537 12.2504 14.5647 12.3749C14.7757 12.4993 15.0382 12.4993 15.5632 12.4993H16.6667"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17.0846 9.99935C16.4446 7.01218 13.9377 4.19827 12.1047 2.4762C10.9549 1.39596 9.2013 1.39596 8.05146 2.4762C5.93431 4.46524 2.91797 7.9109 2.91797 11.3977C2.91797 14.8163 5.62935 18.3327 10.0781 18.3327C12.9056 18.3327 15.0312 16.9123 16.205 14.9993"
                stroke="#7C7F7E"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
            <span>Vitals</span>
          </Styled.Link>
        </Styled.MainMenu>
        <Styled.Content $isStretchContent={isStretchContent}>
          <Outlet context={{ setIsStretchContent }} />
        </Styled.Content>
      </Styled.Layout>
    </Styled.Container>
  );
};

// Medications
// Immunizations
// -------------
// Appointments
// Demographics
// Documents
// DICOM Studies
// Encounters
// Flowsheets
// Insurance
// Orders & Procedures
// Physician Care Team
// Results
// Summary
// Vitals
